import { z } from 'zod';

import { HoldingUnitEventEnvelopeBaseSchema } from './holding-unit-event-envelope.schema.js';

import type {
  FixedIdAssetEvent,
  FixedIdAssetLocationUpdatedEventPayload,
  FixedIdAssetMasterTareUpdatedEventPayload,
  FixedIdAssetRegisteredEventPayload,
  FixedIdAssetStateChangedEventPayload,
} from './fixed-id-assets-events.dto.js';
import { fixedIdAssetEventTypeValues } from './fixed-id-assets-events.dto.js';
import { FixedIdAssetStateSchema } from './fixed-id-assets.schema.js';
import {
  HoldingUnitContainerKindSchema,
  HoldingUnitIdentityModeSchema,
} from './holding-units.schema.js';

export const FixedIdAssetEventTypeSchema = z.enum(fixedIdAssetEventTypeValues);

const FixedIdAssetEventBaseSchema = HoldingUnitEventEnvelopeBaseSchema.extend({
  entityType: z.literal('fixedIdAsset'),
}).omit({ eventType: true, payload: true });

const FixedIdAssetRegisteredEventPayloadSchema: z.ZodType<FixedIdAssetRegisteredEventPayload> =
  z.object({
    identityMode: HoldingUnitIdentityModeSchema.extract(['fixed_asset_cycle']),
    containerKind: HoldingUnitContainerKindSchema.extract(['heavy_container']),
    physicalId: z.string().trim().min(1).max(64),
    normalizedPhysicalId: z.string().trim().min(1).max(64),
    normalizedScannableIdentifier: z.string().trim().min(1).max(64),
    masterTareLb: z.number().nonnegative(),
    state: z.literal('available'),
    homeStoreId: z.uuid(),
    homeLocationId: z.uuid(),
    lastKnownStoreId: z.uuid(),
    lastKnownLocationId: z.uuid(),
    lastKnownYardLocationId: z.uuid().nullable(),
  });

const FixedIdAssetMasterTareUpdatedEventPayloadSchema: z.ZodType<FixedIdAssetMasterTareUpdatedEventPayload> =
  z.object({
    previousMasterTareLb: z.number().nonnegative(),
    masterTareLb: z.number().nonnegative(),
  });

const FixedIdAssetStateChangedEventPayloadSchema: z.ZodType<FixedIdAssetStateChangedEventPayload> =
  z.object({
    fromState: FixedIdAssetStateSchema,
    toState: FixedIdAssetStateSchema,
    activeCycleUnitId: z.uuid().nullable().optional(),
    reason: z.string().trim().min(1).max(500).nullable().optional(),
  });

const FixedIdAssetLocationUpdatedEventPayloadSchema: z.ZodType<FixedIdAssetLocationUpdatedEventPayload> =
  z.object({
    lastKnownStoreId: z.uuid(),
    lastKnownLocationId: z.uuid(),
    lastKnownYardLocationId: z.uuid().nullable(),
  });

export const FixedIdAssetEventSchema: z.ZodType<FixedIdAssetEvent> = z.discriminatedUnion(
  'eventType',
  [
    FixedIdAssetEventBaseSchema.extend({
      eventType: z.literal('fixedIdAssetRegistered'),
      payload: FixedIdAssetRegisteredEventPayloadSchema,
    }),
    FixedIdAssetEventBaseSchema.extend({
      eventType: z.literal('fixedIdAssetMasterTareUpdated'),
      payload: FixedIdAssetMasterTareUpdatedEventPayloadSchema,
    }),
    FixedIdAssetEventBaseSchema.extend({
      eventType: z.literal('fixedIdAssetStateChanged'),
      payload: FixedIdAssetStateChangedEventPayloadSchema,
    }),
    FixedIdAssetEventBaseSchema.extend({
      eventType: z.literal('fixedIdAssetLocationUpdated'),
      payload: FixedIdAssetLocationUpdatedEventPayloadSchema,
    }),
  ],
);
